import { nodeListToArray } from '../helpers/nodeListToArray';
import { enableTabbableChildren } from '../helpers/enableTabbableChildren';
import { disableTabbableChildren } from '../helpers/disableTabbableChildren';
import { getHeightOfHidden } from '../helpers/getHeightOfHidden';

let debounce = require('lodash.debounce');

export class RevealPanel {
    triggers: Array<HTMLButtonElement>;
    panel: HTMLElement;
    isOpen: boolean;
    preventFocus?: boolean;
    clearInputsInside?: boolean;
    openHandlers?: Array<any>;
    afterOpenHandlers?: Array<any>;
    closeHandlers?: Array<any>;
    afterCloseHandlers?: Array<any>;
    breakpoint?: string;
    userHasInteracted: boolean;
    forcedHeight?: string;
    primaryTrigger?: any;

    constructor(panelId: string, preventFocus?: boolean) {
        this.panel = document.querySelector(`#${panelId}`);
        this.triggers = nodeListToArray(document.querySelectorAll(`[aria-controls="${panelId}"]`));
        this.isOpen = false;
        this.preventFocus = preventFocus;
        this.clearInputsInside = this.panel.hasAttribute('data-clear-inputs');
        this.openHandlers = [];
        this.afterOpenHandlers = [];
        this.closeHandlers = [];
        this.afterCloseHandlers = [];
        this.breakpoint = this.panel.hasAttribute('data-default-open-breakpoint') ? this.panel.getAttribute('data-default-open-breakpoint') : '';
        this.userHasInteracted = false;
        this.forcedHeight = this.panel.hasAttribute('data-panel-height') ? this.panel.getAttribute('data-panel-height') : null;


        window.addEventListener(
            'resize',
            debounce(() => {
                this.setPanelHeight();
            }, 100)
        );

        this.triggers.forEach(trigger => {
            if (trigger.hasAttribute('data-panel-primary-trigger')) {
                this.primaryTrigger = trigger;
            }
            trigger.addEventListener('click', this.triggerClick.bind(this));
            setTimeout(() => {
                this.setPanelHeight();
                this.testBreakpoint();
            }, 0);
        });
        this.on('open', () => {
            this.panel.classList.add('transitioning');
        });
        this.on('afterOpen', () => {
            this.panel.classList.remove('transitioning');
        });
        this.on('close', () => {
            this.panel.classList.add('transitioning');
        });
        this.on('afterClose', () => {
            this.panel.classList.remove('transitioning');
            if (this.userHasInteracted) {
                this.triggers[0].focus();
            }
        });
    }

    on(event, handler) {
        switch (event) {
            case "open":
                return this.openHandlers.push(handler);
            case "afterOpen":
                return this.afterOpenHandlers.push(handler);
            case "close":
                return this.closeHandlers.push(handler);
            case "afterClose":
                return this.afterCloseHandlers.push(handler);
        }
    }

    setPanelHeight() {
        if (this.forcedHeight) {
            this.panel.style.height = this.forcedHeight;
        } else {
            const panelHeight = getHeightOfHidden(this.panel);
            this.panel.style.height = `${panelHeight}px`;
        }
    }

    triggerClick(e) {
        this.userHasInteracted = true;
        if (this.isOpen) {
            this.closePanel();
        } else {
            this.openPanel();
        }
    }

    openPanel() {
        let handleOpen = () => {
            if (this.isOpen) {
                this.panel.style.overflow = 'visible';
            }
        }
        if (this.panel.classList.contains('closed')) {
            this.closePanel();
            this.panel.classList.remove('closed');
        } else {
            setTimeout(() => {
                handleOpen();
            }, 300);
            if (this.afterOpenHandlers) {
                this.afterOpenHandlers.forEach(handler => {
                    setTimeout(handler.bind(this), 300);
                });
            }
            if (this.openHandlers) {
                this.openHandlers.forEach(handler => {
                    setTimeout(handler.bind(this), 0);
                })
            }
            this.triggers.forEach(trigger => {
                trigger.setAttribute('aria-expanded', 'true');
            });
            this.panel.setAttribute('aria-hidden', 'false');
            enableTabbableChildren(this.panel);
            this.isOpen = true;
            if (!this.preventFocus) {
                const firstInput = this.panel.querySelector('textarea, select, input:not([type=radio]):not([type=checkbox])') as HTMLInputElement;
                if (firstInput) {
                    firstInput.focus();
                }
            }
        }
    }

    closePanel() {
        this.panel.style.overflow = '';
        this.triggers.forEach(trigger => {
            trigger.setAttribute('aria-expanded', 'false');
        });
        this.panel.setAttribute('aria-hidden', 'true');
        disableTabbableChildren(this.panel);
        this.isOpen = false;

        if (this.clearInputsInside) {
            this.clearInputs();
        }

        if (this.primaryTrigger && this.userHasInteracted) {
            this.primaryTrigger.focus();
        }

        if (this.closeHandlers) {
            this.closeHandlers.forEach(handler => {
                setTimeout(handler.bind(this), 0);
            });
        }

        if (this.afterCloseHandlers) {
            this.afterCloseHandlers.forEach(handler => {
                setTimeout(handler.bind(this), 300);
            });
        }
    }

    clearInputs() {
        // clear checkboxes and radio buttons inside on close, if necessary
        const inputsInside = Array.prototype.slice.call(this.panel.querySelectorAll('input[type="checkbox"], input[type="radio"]')) as HTMLInputElement[];
        inputsInside.forEach(checkOrRadio => {
            checkOrRadio.checked = false;
        });
    }

    testBreakpoint() {
        if (this.breakpoint != '') {
            const query = window.matchMedia(`(max-width: ${this.breakpoint})`);
            if (query.matches) {
                if (this.isOpen !== false && !this.userHasInteracted) {
                    this.closePanel();
                } else { // this check actually makes "start closed" work because it triggers the opn / close check
                    if (!this.isOpen) {
                        this.openPanel();
                    }
                }
            } else if (!this.isOpen) {
                this.openPanel();
            }
        } else if (!this.userHasInteracted) {
            this.closePanel();
        }
    }
}